import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import pick from 'lodash/pick';
import clsx from 'clsx';
import omit from 'lodash/omit';
import {
  PrimaryButton,
  ActionButton,
  Stack,
  TooltipHost,
  TooltipDelay,
  DirectionalHint,
} from 'office-ui-fabric-react';
import { useId } from '@uifabric/react-hooks';
import { DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { useParams } from 'react-router-dom';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import path from 'path-browserify';
import { getFileTypeIconProps } from '@uifabric/file-type-icons';
import {
  DetailsList,
  SelectionMode,
  DetailsRow,
  DetailsHeader,
} from 'office-ui-fabric-react/lib/DetailsList';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import startCase from 'lodash/startCase';
import SectionHeader from '../../components/SectionHeader';
import PageTitle from '../../components/PageTitle';
import styles from './AdjustmentDetails.style';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import Main from '../../components/Main';
import {
  fetchAdjustmentDetails,
  submitExistingAdjustment,
  discardAdjustmentReversal,
  submitReversalAdjustment,
} from '../../actions/adjustments';
import AdjustmentApprovalOptions from '../../components/AdjustmentApprovalOptions';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import FluentGrid from '../../components/FluentGrid';
import useAPI from '../../hooks/useAPI';
import { formatDateString, isAnyLineItemInDraft } from '../../utils';
import { getUser, hasRole } from '../../utils/auth';
import { geographies, adjustmentTypes } from '../../constants';
import { fetchAttachmentFromUrl } from '../../actions/attachments';
import SavingIndicator from '../../components/SavingIndicator';
import AdjustmentActivityList from '../../components/AdjustmentActivityList';
import AdjustmentStatusMessage from '../../components/AdjustmentStatusMessage/AdjustmentStatusMessage';
import AdjustmentStatusIcons from '../../components/AdjustmentStatusIcons';
import {
  AdjustmentStatus,
  hasStatus,
  getAdvancePaymentTotalString,
  getLineItems,
  getAdjustmentListItemsName,
} from '../../utils/adjustmentUtils';
import Pill from '../../components/Pill';
import TextButton from '../../components/TextButton';
import getAdjustmentPaymentStatusInfo from './AdjustmentPaymentStatusInfo';
import PaymentStatusInfoItem from '../../components/PaymentStatusInfoItem';

const formatAmount = amount =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(amount);

const isLineItemReversal = adjustment => {
  return !!adjustment.reversal;
};

const AdjustmentDetails = () => {
  const tooltipId = useId('tooltip');
  const isPartner = hasRole('partner');
  const { adjustmentId } = useParams();
  const [isSubmiting, setisSubmiting] = useState(false);
  const [showDiscardReversalDialog, setShowDiscardReversalDialog] = useState(false);
  const dispatch = useDispatch();
  const user = getUser();
  const { isLoading, error } = useAPI(() => fetchAdjustmentDetails(adjustmentId), [adjustmentId]);
  const discardReversalApi = useAPI(() => discardAdjustmentReversal(adjustmentId), [adjustmentId], {
    fetchOnMount: false,
    autoFetch: false,
    onSuccessCallback() {
      setShowDiscardReversalDialog(false);
    },
  });

  const getAdjustmentTypeText = adjustmentType => {
    let text = startCase(adjustmentType);
    adjustmentTypes.forEach(x => {
      if (x.key === adjustmentType) {
        text = x.text;
      }
    });

    return text;
  };

  const adjustment = useSelector(state => get(state, `entities.adjustments.${adjustmentId}`, null));
  const isAdjustmentInDraftStatus =
    (adjustment && adjustment.status.toLowerCase() === 'draft') || isAnyLineItemInDraft(adjustment);
  let isReviewer = adjustment && user?.profile.oid === adjustment.reviewer?.reviewerId;
  const isApprover = adjustment && user?.profile.oid === adjustment.approver?.approverId;

  if (adjustment) {
    isReviewer =
      (adjustment.status === AdjustmentStatus.ApprovalRequested && isApprover) ||
      (adjustment.status === AdjustmentStatus.ReviewRequested && isReviewer);
  }

  const isCreator = adjustment && user?.profile.oid === adjustment.createdBy;
  const needsApproval =
    adjustment &&
    (adjustment.status.toLowerCase() === 'approvalrequested' ||
      adjustment.status.toLowerCase() === 'reviewrequested' ||
      adjustment.lineItems?.some(
        lineItem =>
          lineItem.status &&
          (lineItem.status.toLowerCase() === 'approvalrequested' ||
            adjustment.status.toLowerCase() === 'reviewrequested'),
      ));

  const showDiscardReversalButton =
    adjustment &&
    adjustment.lineItems?.some(
      lineItem => lineItem.reversal && lineItem.status && lineItem.status.toLowerCase() === 'draft',
    );

  const errorDetail = useMemo(() => {
    if (!error) {
      return null;
    }

    let title = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';

    if (error.status === 404) {
      message = 'This adjustment does not exist. Please try a different link.';
      title = 'Not Found';
    }

    return {
      title,
      message,
    };
  }, [error]);

  const handleDownload = (url, fileName) => () => {
    dispatch(fetchAttachmentFromUrl(url, fileName));
  };

  const attachmentColumns = [
    {
      key: 'fileIcon',
      name: 'File Type',
      iconName: 'Page',
      isIconOnly: true,
      fieldName: 'icon',
      minWidth: 16,
      maxWidth: 16,
      onRender(attachment) {
        const extension = path.extname(attachment.displayName);
        return (
          <FontIcon {...getFileTypeIconProps({ extension, size: 16, imageFileType: 'png' })} />
        );
      },
    },
    {
      key: 'fileName',
      name: 'File Name',
      isRowHeader: true,
      isSorted: true,
      isSortedDescending: false,
      data: 'string',
      isPadded: true,
      onRender(attachment) {
        return <Text>{attachment.displayName}</Text>;
      },
    },
    {
      key: 'view',
      name: 'View',
      fieldName: 'url',
      minWidth: 50,
      maxWidth: 50,
      onRender(attachment) {
        return (
          <ActionButton onClick={handleDownload(attachment.href, attachment.displayName)}>
            View
          </ActionButton>
        );
      },
    },
  ];

  const attachmentRowStyle = {
    fields: {
      alignItems: 'center',
    },
  };

  const royaltiesEarnedColumns = [
    {
      key: 'status',
      name: 'Status',
      ariaLabel: 'Status',
      isIconOnly: false,
      fieldName: 'status',
      minWidth: 52,
      maxWidth: 52,
      columnActionsMode: 0,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        if (isLineItemReversal(lineItem)) {
          return null;
        }

        return <AdjustmentStatusIcons status={lineItem.calculatedUIStatus} />;
      },
    },
    {
      key: 'product',
      name: 'Product',
      fieldName: 'product',
      minWidth: 100,
      maxWidth: 100,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        const name =
          get(lineItem, 'productDetails.name') ||
          get(lineItem, 'pfamDetails.productFamilyName') ||
          '';
        const desc = name ? `${name}` : lineItem.product;
        return <Text className={clsx(isLineItemReversal(lineItem) && styles.hidden)}>{desc}</Text>;
      },
    },
    adjustment?.contract?.silo.toLowerCase() === '1p-azure' && {
      key: 'billToGeography',
      name: 'Bill to Country',
      fieldName: 'billToGeography',
      minWidth: 130,
      maxWidth: 130,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        const geo =
          geographies.find(l => l.key === lineItem.billToGeography)?.text ||
          lineItem.billToGeography;
        return <Text className={clsx(isLineItemReversal(lineItem) && styles.dimText)}>{geo}</Text>;
      },
    },
    {
      key: 'geography',
      name: 'Geography',
      fieldName: 'geography',
      minWidth: 90,
      maxWidth: 90,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        const geo = geographies.find(l => l.key === lineItem.geography)?.text || lineItem.geography;
        return <Text className={clsx(isLineItemReversal(lineItem) && styles.dimText)}>{geo}</Text>;
      },
    },
    {
      key: 'amount',
      name: 'Amount',
      fieldName: 'amount',
      minWidth: 90,
      maxWidth: 90,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        const isNegative = Number.isFinite(lineItem.amount) && lineItem.amount < 0;
        return (
          <Text className={clsx(isNegative && styles.negativeAmount)}>
            {formatAmount(lineItem.amount) || 'None'}
          </Text>
        );
      },
    },
    {
      key: 'currency',
      name: 'Currency',
      minWidth: 120,
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        return (
          <Text className={clsx(isLineItemReversal(lineItem) && styles.dimText)}>
            {lineItem.currency && lineItem.currency.name
              ? `${lineItem.currency.code} - ${lineItem.currency.name}`
              : lineItem.currency?.code}
          </Text>
        );
      },
    },
    {
      key: 'actualDate',
      name: 'Actual Date',
      fieldName: 'actualDate',
      headerClassName: styles.transparentBackground,
      onRender(lineItem) {
        return (
          <Text className={clsx(isLineItemReversal(lineItem) && styles.dimText)}>
            {(lineItem.actualDate && formatDateString(lineItem.actualDate)) || ''}
          </Text>
        );
      },
    },
  ].filter(col => !!col);

  const requiredLineItemFieldsForRoyaltiesEarnedNonAzureAdjustment = [
    'amount',
    'product',
    'actualDate',
    'geography',
    'currency',
  ];

  const requiredLineItemFieldsForRoyaltiesEarnedAzureAdjustment = [
    'amount',
    'product',
    'actualDate',
    'billToGeography',
    'geography',
    'currency',
  ];

  const requiredLineItemFieldsForRecoupablePayment = [
    'amount',
    'invoiceNumber',
    'currency',
    'invoiceDate',
    'statementPeriod',
    'dueDate',
    'taxCode',
  ];

  const requiredLineItemFieldsForNonRecoupablePayment = [
    'amount',
    'invoiceNumber',
    'currency',
    'invoiceDate',
    'dueDate',
    'taxCode',
    'expenseAccount',
    'costCenter',
  ];

  const requiredLineItemFieldsForAdvanceAdjustment = ['amount', 'adjustmentDate', 'currency'];

  const requiredLineItemsMap = {
    AdjustmentToRoyaltiesEarned:
      adjustment?.contract?.silo === '1p-azure'
        ? requiredLineItemFieldsForRoyaltiesEarnedAzureAdjustment
        : requiredLineItemFieldsForRoyaltiesEarnedNonAzureAdjustment,
    RecoupablePayment: requiredLineItemFieldsForRecoupablePayment,
    NonRecoupablePayment: requiredLineItemFieldsForNonRecoupablePayment,
    AdvanceBalance: requiredLineItemFieldsForAdvanceAdjustment,
  };

  const checkIsLineItemIncomplete = (requiredLineItemFields, lineItem) => {
    return requiredLineItemFields.some(field => !lineItem[field] || lineItem[field] === '');
  };
  const areLineItemsComplete = adjustmentObj => {
    if (!adjustmentObj.lineItems || adjustmentObj.lineItems.length === 0) {
      return false;
    }

    const areLineItemsCompleteVal = !adjustmentObj.lineItems.some(
      checkIsLineItemIncomplete.bind(null, get(requiredLineItemsMap, adjustmentObj.type)),
    );
    return adjustmentObj.type === 'RecoupablePayment' || adjustmentObj.type === 'AdvanceBalance'
      ? adjustmentObj.advanceGroupId && areLineItemsCompleteVal
      : areLineItemsCompleteVal;
  };

  let enableSubmit = true;
  if (adjustment && adjustment.status.toLowerCase() === 'draft') {
    enableSubmit = adjustment.approver?.approverId && areLineItemsComplete(adjustment);
  }

  const pickRequiredAdjustmentFields = adjustmentObj => {
    let adjustmentValues = pick(adjustmentObj, [
      'name',
      'type',
      'contractId',
      'comments',
      'reviewer',
      'approver',
    ]);

    switch (adjustmentObj.type) {
      case 'AdjustmentToRoyaltiesEarned':
        adjustmentValues = {
          ...adjustmentValues,
          ...pick(adjustmentObj, ['autoReverse', 'reverseDate']),
        };
        break;
      case 'RecoupablePayment':
      case 'AdvanceBalance':
        adjustmentValues = {
          ...adjustmentValues,
          ...pick(adjustmentObj, ['advanceGroupId']),
        };
        break;
      default:
        break;
    }

    const adjustmentListItemsName = getAdjustmentListItemsName(adjustment.type);
    adjustmentValues[adjustmentListItemsName] = adjustmentObj.lineItems;
    adjustmentValues.lineItems = getLineItems(adjustmentValues, adjustmentValues.type);
    adjustmentValues = omit(adjustmentValues, [adjustmentListItemsName]);

    adjustmentValues.reviewer = pick(adjustmentValues.reviewer, [
      'reviewerId',
      'reviewerJustification',
    ]);
    adjustmentValues.approver = pick(adjustmentValues.approver, [
      'approverId',
      'approverJustification',
    ]);

    return adjustmentValues;
  };

  const handleSubmit = async () => {
    setisSubmiting(true);

    if (adjustment.calculatedUIStatus === AdjustmentStatus.ReversalDraft) {
      await dispatch(
        submitReversalAdjustment(
          {
            lineItems: adjustment.lineItems
              .filter(lineItem => !!lineItem.reversal)
              .filter(lineItem => hasStatus(lineItem, AdjustmentStatus.Draft))
              .map(lineItem => ({
                id: lineItem.reversal.lineItemId,
                actualDate: lineItem.actualDate,
              })),

            reviewer: adjustment.reviewer,
          },
          adjustment._id,
        ),
      );
    } else {
      await dispatch(
        submitExistingAdjustment(pickRequiredAdjustmentFields(adjustment), adjustmentId),
      );
    }

    setisSubmiting(false);
  };

  const discardReversalClicked = () => {
    setShowDiscardReversalDialog(true);
  };

  const discardReversalSubmitted = () => {
    discardReversalApi.fetchApi();
  };

  if (isLoading) {
    return (
      <Spinner
        data-testid="adjustmentDetailsSpinner"
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
      />
    );
  }

  if (errorDetail) {
    return (
      <>
        <PageTitle>{errorDetail.title}</PageTitle>
        <Main>
          <Text>{errorDetail.message}</Text>
        </Main>
      </>
    );
  }

  const renderAmount = lineItem => {
    const amount = get(lineItem, 'amount', 0);

    // if amount is negative, show in red with parentheses
    if (amount < 0) {
      return (
        <Text as="p" variant="small" className={styles.negativeAmount}>
          {`(${formatAmount(Math.abs(amount))})`}
        </Text>
      );
    }

    return (
      <Text as="p" variant="small">
        {formatAmount(amount) || ''}
      </Text>
    );
  };

  const productDetails = lineItem => {
    return (
      <FluentGrid col sm={6} md={3}>
        <SectionHeader noBorder noSpacing>
          Product
        </SectionHeader>
        <Text as="p" variant="small">
          {get(lineItem, 'productDetails.name') ||
            get(lineItem, 'pfamDetails.productFamilyName') ||
            ''}
        </Text>
      </FluentGrid>
    );
  };

  const renderPaymentStatusCard = paymentStatus => {
    if (paymentStatus) {
      const { primaryStatus, secondaryStatus, paymentDate, invoiceId, paymentReferenceNumber } =
        paymentStatus;

      if (primaryStatus && primaryStatus !== 'Not Available') {
        const isPaymentSent = primaryStatus && primaryStatus === 'PaymentSent';
        const paymentStatusInfoDetails = getAdjustmentPaymentStatusInfo(
          primaryStatus,
          secondaryStatus,
          paymentDate,
          invoiceId || null,
          paymentReferenceNumber || null,
          isPartner,
        );

        return (
          <div>
            <span
              className={
                isPaymentSent ? styles.paymentStatusSentIcon : styles.paymentStatusInProgressIcon
              }
            >
              <FontIcon
                iconName={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
                arianame={isPaymentSent ? 'NavigateBackMirrored' : 'recent'}
              />
            </span>
            <Text variant="small" className={styles.paymentStatusDate}>
              {isPaymentSent ? 'Sent' : 'Scheduled'}{' '}
              {isPaymentSent ? formatDateString(paymentDate, 'MMM d, yyyy') : ''}
            </Text>
            {paymentStatusInfoDetails && (
              <TooltipHost
                id={tooltipId}
                content={PaymentStatusInfoItem({
                  paymentStatusInfoDetails: paymentStatusInfoDetails.paymentStatusInfo,
                  invoiceDetails: paymentStatusInfoDetails.invoice,
                  paymentReferenceDetails: paymentStatusInfoDetails.paymentReference,
                })}
                delay={TooltipDelay.zero}
                directionalHint={DirectionalHint.topCenter}
                closeDelay={500}
                className={styles.toolTipHost}
                calloutProps={styles.callout}
                tooltipProps={styles.toolTip}
              >
                <TextButton tabIndex="0" noPadding aria-describedby={tooltipId}>
                  <Text variant="small" className={styles.paymentDetailsButton}>
                    Details
                  </Text>
                </TextButton>
              </TooltipHost>
            )}
          </div>
        );
      }

      return (
        <div>
          <span className={styles.paymentStatusInProgressIcon}>
            <FontIcon iconName="recent" arianame="recent" />
          </span>
          <Text variant="small" className={styles.paymentStatusDate}>
            In Process
          </Text>
        </div>
      );
    }

    return <span />;
  };

  return (
    <>
      {adjustment && adjustment.contract && (
        <>
          <PageTitle>{adjustment.name}</PageTitle>
          <Main>
            <AdjustmentStatusMessage adjustment={adjustment} isReviewer={isReviewer} />
            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader variant="large" noBorder noSpacing id="adjustmentDetailsHeading">
                    Adjustment Details
                  </SubModuleHeader>

                  <SubModuleContent>
                    <FluentGrid className={styles.fluentRow}>
                      <FluentGrid row>
                        <FluentGrid col sm={8} md={4} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Adjustment Type
                          </SectionHeader>
                          <Text as="p" variant="small" data-testid="adjustment-type">
                            {getAdjustmentTypeText(adjustment.type)}
                          </Text>
                        </FluentGrid>
                      </FluentGrid>
                    </FluentGrid>

                    <FluentGrid className={styles.fluentRow}>
                      <FluentGrid row>
                        <FluentGrid col sm={8} md={4} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Created Date
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {(adjustment.createdTimestamp &&
                              formatDateString(adjustment.createdTimestamp)) ||
                              ''}
                          </Text>
                        </FluentGrid>

                        {adjustment.status === AdjustmentStatus.Approved && (
                          <FluentGrid col sm={8} md={4} className={styles.keywords}>
                            <SectionHeader noBorder noSpacing>
                              Approved Date
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {(adjustment.approvedTimestamp &&
                                formatDateString(adjustment.approvedTimestamp)) ||
                                ''}
                            </Text>
                          </FluentGrid>
                        )}
                      </FluentGrid>
                    </FluentGrid>

                    <FluentGrid className={styles.fluentRow}>
                      <FluentGrid row>
                        <FluentGrid col sm={8} md={4} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Contract ID
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.contract.contractId}
                          </Text>
                        </FluentGrid>

                        <FluentGrid col sm={8} md={4} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Contract Name
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.contract.name}
                          </Text>
                        </FluentGrid>
                      </FluentGrid>
                    </FluentGrid>

                    {adjustment.type === 'AdjustmentToRoyaltiesEarned' && (
                      <FluentGrid className={styles.fluentRow}>
                        <FluentGrid row>
                          <FluentGrid col sm={8} md={4} className={styles.keywords}>
                            <SectionHeader noBorder noSpacing>
                              Auto Reverse
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {adjustment.autoReverse ? 'Yes' : 'No'}
                            </Text>
                          </FluentGrid>

                          <FluentGrid col sm={8} md={4} className={styles.keywords}>
                            <SectionHeader noBorder noSpacing>
                              Reverse Date
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {(adjustment.reverseDate &&
                                formatDateString(adjustment.reverseDate)) ||
                                'N/A'}
                            </Text>
                          </FluentGrid>
                        </FluentGrid>
                      </FluentGrid>
                    )}

                    <FluentGrid className={styles.fluentRow}>
                      <FluentGrid row>
                        <FluentGrid col sm={24} md={12}>
                          <SectionHeader noBorder noSpacing>
                            Business Justification
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.comments?.businessJustification || 'None'}
                          </Text>
                        </FluentGrid>
                      </FluentGrid>
                    </FluentGrid>

                    {adjustment.type === 'AdjustmentToRoyaltiesEarned' && (
                      <FluentGrid className={styles.fluentRow}>
                        <FluentGrid row>
                          <FluentGrid col sm={24} md={12}>
                            <SectionHeader noBorder noSpacing>
                              Statement Comment
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {adjustment.comments?.statementComment || 'None'}
                            </Text>
                          </FluentGrid>
                        </FluentGrid>
                      </FluentGrid>
                    )}
                    {adjustment.keywords && adjustment.keywords.length > 0 && (
                      <FluentGrid col sm={12} md={6}>
                        <FluentGrid row>
                          <SectionHeader noBorder noSpacing>
                            Keywords
                          </SectionHeader>

                          <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                            {adjustment.keywords.map(keyword => (
                              <Pill key={keyword}>{`#${keyword.trim()}`}</Pill>
                            ))}
                          </Stack>
                        </FluentGrid>
                      </FluentGrid>
                    )}

                    {adjustment.attachments && adjustment.attachments.length > 0 && (
                      <>
                        <SectionHeader>Attachments</SectionHeader>

                        <DetailsList
                          className={styles.transparentBackground}
                          columns={attachmentColumns}
                          items={adjustment.attachments}
                          isHeaderVisible={false}
                          selectionMode={SelectionMode.none}
                          onRenderRow={props => (
                            <DetailsRow
                              {...props}
                              className={styles.transparentBackground}
                              styles={attachmentRowStyle}
                            />
                          )}
                        />
                      </>
                    )}
                  </SubModuleContent>
                </SubModule>

                {adjustment.type === 'AdjustmentToRoyaltiesEarned' ? (
                  <SubModule disableAccordian>
                    <SubModuleHeader noBorder>
                      {getAdjustmentTypeText(adjustment.type)}
                    </SubModuleHeader>

                    <SubModuleContent>
                      <DetailsList
                        className={styles.transparentBackground}
                        ariaLabelForListHeader={getAdjustmentTypeText(adjustment.type)}
                        ariaLabelForGrid={getAdjustmentTypeText(adjustment.type)}
                        columns={royaltiesEarnedColumns}
                        items={adjustment.lineItems}
                        selectionMode={SelectionMode.none}
                        onRenderDetailsHeader={props => (
                          <DetailsHeader {...props} className={styles.transparentBackground} />
                        )}
                        onRenderRow={props => (
                          <DetailsRow
                            {...props}
                            className={
                              isLineItemReversal(get(props, 'item'))
                                ? styles.dimText
                                : styles.transparentBackground
                            }
                          />
                        )}
                      />
                    </SubModuleContent>
                  </SubModule>
                ) : (
                  <>
                    <SubModule disableAccordian>
                      <SubModuleHeader
                        variant="large"
                        extra={getAdvancePaymentTotalString(adjustment.lineItems, styles)}
                        noBorder
                      >
                        {getAdjustmentTypeText(adjustment.type)}
                      </SubModuleHeader>
                    </SubModule>
                    {adjustment.lineItems.map((lineItem, index) => (
                      <SubModule>
                        <SubModuleHeader id={`Payment-${index + 1}`}>
                          {adjustment.type === 'AdvanceBalance'
                            ? `Adjustment #${index + 1}`
                            : `Payment #${index + 1}`}
                        </SubModuleHeader>
                        <SubModuleContent>
                          {/* Row 1 */}
                          <FluentGrid className={styles.fluentRow}>
                            <FluentGrid row>
                              {adjustment.type !== 'AdvanceBalance' && (
                                <>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Payment Status
                                    </SectionHeader>
                                    {renderPaymentStatusCard(lineItem.paymentStatus)}
                                  </FluentGrid>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Invoice Number
                                    </SectionHeader>
                                    <Text as="p" variant="small">
                                      {lineItem.invoiceNumber || ''}
                                    </Text>
                                  </FluentGrid>
                                </>
                              )}

                              {adjustment.type === 'AdvanceBalance' && (
                                <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                  <SectionHeader noBorder noSpacing>
                                    Adjustment Date
                                  </SectionHeader>
                                  <Text as="p" variant="small">
                                    {(lineItem.adjustmentDate &&
                                      formatDateString(lineItem.adjustmentDate)) ||
                                      ''}
                                  </Text>
                                </FluentGrid>
                              )}

                              <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                <SectionHeader noBorder noSpacing>
                                  Amount
                                </SectionHeader>
                                <Text as="p" variant="small">
                                  {renderAmount(lineItem)}
                                </Text>
                              </FluentGrid>
                              <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                <SectionHeader noBorder noSpacing>
                                  Currency
                                </SectionHeader>
                                <Text as="p" variant="small">
                                  {lineItem.currency?.code && lineItem.currency?.name
                                    ? `${lineItem.currency.code} - ${lineItem.currency.name}`
                                    : ''}
                                </Text>
                              </FluentGrid>
                            </FluentGrid>
                          </FluentGrid>

                          {/* Row 2 */}
                          <FluentGrid className={styles.fluentRow}>
                            <FluentGrid row>
                              {adjustment.type !== 'AdvanceBalance' && (
                                <>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Invoice Date
                                    </SectionHeader>
                                    <Text as="p" variant="small">
                                      {(lineItem.invoiceDate &&
                                        formatDateString(lineItem.invoiceDate)) ||
                                        ''}
                                    </Text>
                                  </FluentGrid>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Due Date
                                    </SectionHeader>
                                    <Text as="p" variant="small">
                                      {(lineItem.dueDate && formatDateString(lineItem.dueDate)) ||
                                        ''}
                                    </Text>
                                  </FluentGrid>
                                </>
                              )}

                              {adjustment.type !== 'NonRecoupablePayment' && (
                                <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                  <SectionHeader noBorder noSpacing>
                                    Statement Period
                                  </SectionHeader>
                                  <Text as="p" variant="small">
                                    {(lineItem.statementPeriod &&
                                      formatDateString(lineItem.statementPeriod, 'MM/yyyy')) ||
                                      ''}
                                  </Text>
                                </FluentGrid>
                              )}
                              {adjustment.type !== 'RecoupablePayment' && productDetails(lineItem)}
                            </FluentGrid>
                          </FluentGrid>

                          {/* Row 3 */}
                          {adjustment.type !== 'AdvanceBalance' && (
                            <FluentGrid className={styles.fluentRow}>
                              <FluentGrid row>
                                <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                  <SectionHeader noBorder noSpacing>
                                    Tax Code
                                  </SectionHeader>
                                  <Text as="p" variant="small">
                                    {lineItem.taxCode || ''}
                                  </Text>
                                </FluentGrid>
                                {adjustment.type === 'RecoupablePayment' &&
                                  productDetails(lineItem)}

                                {adjustment.type === 'NonRecoupablePayment' && (
                                  <>
                                    <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                      <SectionHeader noBorder noSpacing>
                                        Expense Account
                                      </SectionHeader>
                                      <Text as="p" variant="small">
                                        {lineItem.expenseAccount || ''}
                                      </Text>
                                    </FluentGrid>
                                    <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                      <SectionHeader noBorder noSpacing>
                                        Cost Center/Internal Order
                                      </SectionHeader>
                                      <Text as="p" variant="small">
                                        {lineItem.costCenter || ''}
                                      </Text>
                                    </FluentGrid>
                                  </>
                                )}
                              </FluentGrid>
                            </FluentGrid>
                          )}

                          {/* Row 4 */}
                          <FluentGrid className={styles.fluentRow}>
                            <FluentGrid row>
                              <FluentGrid col sm={24} md={12} className={styles.keywords}>
                                <SectionHeader noBorder noSpacing>
                                  Description
                                </SectionHeader>
                                <Text as="p" variant="small">
                                  {lineItem.description || ''}
                                </Text>
                              </FluentGrid>
                            </FluentGrid>
                          </FluentGrid>

                          {/* Row 5 */}
                          <FluentGrid className={styles.fluentRow}>
                            <FluentGrid row>
                              <FluentGrid col sm={24} md={12} className={styles.keywords}>
                                <SectionHeader noBorder noSpacing>
                                  Statement Comment
                                </SectionHeader>
                                <Text as="p" variant="small">
                                  {lineItem.statementComment || ''}
                                </Text>
                              </FluentGrid>
                            </FluentGrid>
                          </FluentGrid>

                          {/* Row 6 */}
                          {adjustment.type !== 'AdvanceBalance' && (
                            <FluentGrid className={styles.fluentRow}>
                              <FluentGrid row>
                                <>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Invoice Approver
                                    </SectionHeader>
                                    <Text as="p" variant="small">
                                      {lineItem.invoiceApprover?.name || ''}
                                    </Text>
                                  </FluentGrid>
                                  <FluentGrid col sm={6} md={3} className={styles.keywords}>
                                    <SectionHeader noBorder noSpacing>
                                      Interim Invoice Approver
                                    </SectionHeader>
                                    <Text as="p" variant="small">
                                      {lineItem.interimInvoiceApprover?.name || ''}
                                    </Text>
                                  </FluentGrid>
                                </>
                              </FluentGrid>
                            </FluentGrid>
                          )}
                        </SubModuleContent>
                      </SubModule>
                    ))}
                  </>
                )}
              </ModuleContent>
            </Module>

            {(adjustment.type === 'RecoupablePayment' || adjustment.type === 'AdvanceBalance') && (
              <Module>
                <ModuleContent>
                  <SubModule disableAccordian>
                    <SubModuleHeader noBorder noSpacing id="adjustmentDetailsHeading">
                      Recoupment Terms
                    </SubModuleHeader>

                    <SubModuleContent>
                      <FluentGrid>
                        <FluentGrid row>
                          <FluentGrid col sm={12} md={6} className={styles.keywords}>
                            <SectionHeader noBorder noSpacing>
                              Advance Group
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {adjustment.advanceGroupDetails?.name || ''}
                            </Text>
                          </FluentGrid>
                          <FluentGrid col sm={12} md={6} className={styles.keywords}>
                            <SectionHeader noBorder noSpacing>
                              Advance Account
                            </SectionHeader>
                            <Text as="p" variant="small">
                              {adjustment.advanceGroupDetails?.accountId || ''}
                            </Text>
                          </FluentGrid>
                        </FluentGrid>
                      </FluentGrid>
                    </SubModuleContent>
                  </SubModule>
                </ModuleContent>
              </Module>
            )}

            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader noBorder noSpacing id="adjustmentDetailsHeading">
                    Reviewer
                  </SubModuleHeader>

                  <SubModuleContent>
                    <FluentGrid>
                      <FluentGrid row>
                        <FluentGrid col sm={12} md={6} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Reviewer
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.reviewer?.name || adjustment.reviewer?.reviewerId || 'None'}
                          </Text>
                        </FluentGrid>
                        <FluentGrid col sm={12} md={6} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Reviewer Justification
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.reviewer?.reviewerJustification || 'None'}
                          </Text>
                        </FluentGrid>
                      </FluentGrid>
                    </FluentGrid>
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>

            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader noBorder noSpacing id="adjustmentDetailsHeading">
                    Approver
                  </SubModuleHeader>

                  <SubModuleContent>
                    <FluentGrid>
                      <FluentGrid row>
                        <FluentGrid col sm={12} md={6} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Approver
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.approver?.name || adjustment.approver?.approverId || 'None'}
                          </Text>
                        </FluentGrid>
                        <FluentGrid col sm={12} md={6} className={styles.keywords}>
                          <SectionHeader noBorder noSpacing>
                            Approver Justification
                          </SectionHeader>
                          <Text as="p" variant="small">
                            {adjustment.approver?.approverJustification || 'None'}
                          </Text>
                        </FluentGrid>
                      </FluentGrid>
                    </FluentGrid>
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>

            <div className={styles.buttonContainer}>
              {showDiscardReversalButton && (
                <DefaultButton
                  disabled={isSubmiting || (isReviewer && !isCreator)}
                  onClick={discardReversalClicked}
                  data-testid="discard-adjustment-reversal"
                  className={styles.saveDraftButton}
                >
                  Discard Reversal
                </DefaultButton>
              )}

              {isAdjustmentInDraftStatus &&
                (!isSubmiting ? (
                  <PrimaryButton
                    type="submit"
                    data-testid="submitAdjustment"
                    onClick={handleSubmit}
                    disabled={!enableSubmit || (isReviewer && !isCreator)}
                  >
                    Submit for Approval
                  </PrimaryButton>
                ) : (
                  <SavingIndicator data-testid="submitLoader" className={styles.saveDraftButton} />
                ))}
            </div>

            <AdjustmentApprovalOptions
              adjustment={adjustment}
              isVisible={needsApproval && isReviewer}
            />

            <AdjustmentActivityList adjustmentId={adjustmentId} />

            <Dialog
              dialogContentProps={{
                type: DialogType.normal,
                title: 'Discard reversal request?',
                closeButtonAriaLabel: 'Close',
              }}
              hidden={!showDiscardReversalDialog}
              onDismiss={() => setShowDiscardReversalDialog(false)}
              isSubmitting={false}
            >
              <Label>
                Discarding will remove the requested reversing line items. Do you wish to continue?
              </Label>

              {discardReversalApi.error && (
                <Label className={styles.textColorError}>
                  An error occurred while discarding the reversal, please try again.
                </Label>
              )}

              <DialogFooter>
                <DefaultButton
                  data-testid="discard-reversal-cancel-button"
                  onClick={() => setShowDiscardReversalDialog(false)}
                  disabled={discardReversalApi.isLoading}
                >
                  Cancel
                </DefaultButton>
                <PrimaryButton
                  type="submit"
                  data-testid="discard-reversal-submit-button"
                  disabled={discardReversalApi.isLoading}
                  onClick={discardReversalSubmitted}
                >
                  Discard Reversal
                  {discardReversalApi.isLoading && (
                    <Spinner
                      data-testid="discard-reversal-spinner"
                      size={SpinnerSize.medium}
                      className={styles.discardReversalLoadingSpinner}
                    />
                  )}
                </PrimaryButton>
              </DialogFooter>
            </Dialog>
          </Main>
        </>
      )}
    </>
  );
};

export default AdjustmentDetails;
